import './book.css';
import type { GameState, StateArea, StateSpot, SubmitResult } from '../../api/types';
import type { Tab, TabContext, TabFactory, TabShowOptions } from '../contract';
import { geo } from '../../core/geo';
import { store } from '../../core/store';
import { openView } from '../../core/viewlog';
import { outbox, type SendOutcome } from '../../core/outbox';
import { fmtRemaining, remainingMs, serverNow } from '../../core/clock';
import { diceRequestFrom } from '../../flows/submit/dice';
import { isOwnedByFlow, whenFlowIdle } from '../../flows/submit/owned';
import { codeOf, settledNoticeBody, SETTLED_NOTICE_HEADING } from '../../flows/submit/errors';
import { displayLog } from './displayLog';
import {
  PAGE_SIZE,
  areaColorCss,
  bookImageIndex,
  chipLabels,
  clearedCount,
  deriveStatus,
  fmtDistance,
  orderByDistance,
  paginate,
  positionOf,
  roman,
  sealAngle,
  sealVisible,
  submitButtonState,
  swipeDirection,
  distanceM,
  type AreaStatus,
} from './logic';

function h<K extends keyof HTMLElementTagNameMap>(tag: K, cls?: string, text?: string): HTMLElementTagNameMap[K] {
  const e = document.createElement(tag);
  if (cls) e.className = cls;
  if (text !== undefined) e.textContent = text;
  return e;
}

const sleep = (ms: number): Promise<void> => new Promise((r) => setTimeout(r, ms));
const OPEN_MS = 750;
const ease = { io: (x: number): number => (x < 0.5 ? 4 * x * x * x : 1 - Math.pow(-2 * x + 2, 3) / 2) };

interface Layout {
  areaId: string;
  /** 表示した順の spot_id */
  order: number[];
}

class BookTab implements Tab {
  readonly id = 'book' as const;
  readonly el = h('div', 'bk-root');

  private book = h('div', 'bk-book');
  private page = h('div', 'bk-page');
  private head = h('div', 'bk-head');
  private list = h('div', 'bk-list');
  private pager = h('div', 'bk-pager');
  private dpage = h('div', 'bk-dpage');
  private marks = h('div', 'bk-marks');
  private opn = h('div', 'bk-opn');
  private cover = h('div', 'bk-cover');
  private shade = h('div', 'bk-shade');

  private state: GameState | null = null;
  private areaId: string | null = null;
  private layout: Layout | null = null;
  private curPage = 0;
  private view: 'toc' | 'detail' = 'toc';
  private detailId: number | null = null;
  private closeView: ((reason?: string) => void) | null = null;
  private viewSeq = 0;
  private visible = false;
  private dirty = false;
  private submitting = false;
  private locks = 0;
  private turning = false;
  private pendingSeal = new Set<number>();
  private sheets: HTMLElement[] = [];
  private marksKey = '';
  private imgKey = '';
  private listKey = '';
  private seals = new Map<number, HTMLImageElement>();
  /** 本の画像（book_w1〜w5）を先に読んでおく。切り替えた瞬間に読み込むと、本が一瞬消えてから出る */
  private bookImgs = new Map<string, HTMLImageElement>();
  private tick: ReturnType<typeof setInterval> | null = null;
  private chain: Promise<void> = Promise.resolve();
  private raf = 0;

  constructor(private ctx: TabContext) {
    this.el.dataset.sbLayout = 'book'; // 提出の流れ（sb-）に図鑑の頁の位置へ出すよう伝える
    this.buildSkeleton();
    for (let i = 1; i <= 5; i++) {
      const key = `book/book_w${i}.webp`;
      const im = new Image();
      im.src = this.ctx.asset(key);
      this.bookImgs.set(key, im);
    }
    this.state = store.state;
    geo.changed.on(() => {
      if (this.visible && this.view === 'toc') this.refreshDistances();
    });
    outbox.settled.on((out) => {
      if (out.item.kind !== 'spot' || isOwnedByFlow(out.item.id)) return;
      this.chain = this.chain.then(() => this.onSettled(out)).catch((e) => console.error(e));
    });
  }

  // ------------------------------------------------------------ Tab

  async show(opts: TabShowOptions): Promise<void> {
    this.visible = true;
    const f = opts.focus;
    if (f?.spotId != null) {
      const sp = this.state?.spots.find((s) => s.spot_id === f.spotId);
      if (sp) this.setArea(sp.area_id);
    } else if (f?.areaId) this.setArea(f.areaId);
    this.layout = null; // 目次を開き直したときに並べ直す（現在地から近い順）
    this.render();
    if (f?.spotId != null) this.openDetail(f.spotId, false);
    this.startTick();
    if (opts.animate) await this.playOpen();
  }

  hide(): void {
    this.visible = false;
    if (this.tick) clearInterval(this.tick);
    this.tick = null;
    if (this.view === 'detail' && !this.submitting) this.closeDetail('tab_change');
  }

  update(state: GameState, _prev: GameState | null): void {
    this.state = state;
    if (!this.visible || this.locks > 0) {
      this.dirty = true;
      return;
    }
    this.render();
  }

  // ------------------------------------------------------------ 骨組み

  private buildSkeleton(): void {
    this.head.setAttribute('aria-live', 'polite');
    this.pager.innerHTML =
      '<button type="button" class="pv">前の頁</button><span class="pn"><span class="bk-num cur">1</span>／<span class="bk-num tot">1</span></span><button type="button" class="nx">次の頁</button>';
    this.pager.querySelector('.pv')?.addEventListener('click', () => this.turn(-1));
    this.pager.querySelector('.nx')?.addEventListener('click', () => this.turn(1));
    this.page.append(this.head, this.list, this.pager);
    this.book.append(this.page, this.dpage);
    this.book.dataset.view = 'toc';
    this.marks.setAttribute('role', 'group');
    this.marks.setAttribute('aria-label', 'エリア');

    // ㉗ 左右にはらってもめくれる（横40px以上かつ縦の1.5倍）
    let sx: number | null = null;
    let sy = 0;
    let swiped = false;
    this.list.addEventListener('pointerdown', (e) => {
      sx = e.clientX;
      sy = e.clientY;
    });
    this.list.addEventListener('pointerup', (e) => {
      if (sx === null) return;
      const dir = swipeDirection(e.clientX - sx, e.clientY - sy);
      sx = null;
      if (dir) {
        swiped = true;
        setTimeout(() => (swiped = false), 0);
        this.turn(dir === 'next' ? 1 : -1);
      }
    });
    this.list.addEventListener('pointercancel', () => (sx = null));
    this.list.addEventListener(
      'click',
      (e) => {
        if (swiped) e.stopPropagation();
      },
      true,
    );

    // ㉝ 表紙
    this.opn.hidden = true;
    this.opn.setAttribute('aria-hidden', 'true');
    const grow = h('div', 'bk-grow');
    const front = h('div', 'front');
    front.append(h('span', 'ttl', '図　鑑'));
    this.cover.append(front, h('div', 'back'));
    grow.append(this.cover);
    this.opn.append(grow, this.shade);

    this.el.append(this.book, this.marks, this.opn);
  }

  // ------------------------------------------------------------ 描画（目次）

  private areas(): StateArea[] {
    return this.state?.areas ?? [];
  }

  private setArea(id: string): void {
    if (this.areaId === id) return;
    this.areaId = id;
    this.curPage = 0;
    this.layout = null;
  }

  private render(): void {
    this.dirty = false;
    const st = this.state;
    if (!st || !st.areas.length) {
      this.head.replaceChildren();
      this.list.replaceChildren();
      return;
    }
    if (!this.areaId || !st.areas.some((a) => a.area_id === this.areaId)) {
      this.areaId = st.areas[0]?.area_id ?? null;
      this.layout = null;
    }
    const area = st.areas.find((a) => a.area_id === this.areaId);
    if (!area) return;
    const idx = st.areas.indexOf(area);
    const imgKey = `book/book_w${bookImageIndex(idx)}.webp`;
    if (imgKey !== this.imgKey) {
      this.imgKey = imgKey;
      const im = this.bookImgs.get(imgKey);
      const show = (): void => {
        if (this.imgKey === imgKey) this.book.style.backgroundImage = `url(${this.ctx.asset(imgKey)})`;
      };
      // 読み終わるまでは前の本の絵のまま（消して白くしない）
      if (!im || (im.complete && im.naturalWidth)) show();
      else im.decode().then(show, show);
    }
    this.renderMarks();
    const status = deriveStatus(st, area.area_id, serverNow());
    this.renderHead(area, idx, status);
    this.ensureLayout(area);
    this.renderList(status);
    if (this.view === 'detail' && this.detailId != null) this.renderDetail();
  }

  private renderMarks(): void {
    const areas = this.areas();
    const key = areas.map((a) => `${a.area_id}|${a.name}|${a.color}`).join(',');
    if (key === this.marksKey) {
      // エリアを替えただけなら紐は作り直さない（作り直すと伸び縮みの動きが出ない）
      this.marks.querySelectorAll<HTMLElement>('.bk-mk').forEach((b, i) => {
        if (areas[i]?.area_id === this.areaId) b.setAttribute('aria-current', 'page');
        else b.removeAttribute('aria-current');
      });
      return;
    }
    this.marksKey = key;
    const btns = areas.map((a, i) => {
      const b = h('button', 'bk-mk');
      b.type = 'button';
      b.style.setProperty('--c', areaColorCss(a.color, i));
      b.style.setProperty('--i', String(i));
      b.style.setProperty('--n', String(areas.length));
      if (a.area_id === this.areaId) b.setAttribute('aria-current', 'page');
      b.append(h('span', 'nm', a.name));
      b.addEventListener('click', () => this.onMark(a.area_id));
      return b;
    });
    this.marks.replaceChildren(...btns);
  }

  private onMark(areaId: string): void {
    if (this.submitting) return;
    if (this.view === 'detail') this.closeDetail('area_change');
    this.setArea(areaId);
    // 頁はそのまま差し替える。11/3 に入れた薄く入れ替える動き（0.25→1）は「一瞬消えて出る」に見えたのでやめた（本人11/4）
    this.render();
  }

  private renderHead(area: StateArea, idx: number, status: AreaStatus): void {
    const spots = this.state?.spots.filter((s) => s.area_id === area.area_id) ?? [];
    const { cleared, total } = clearedCount(spots);
    const cnt = h('span', 'bk-cnt');
    cnt.append(document.createTextNode('スポット '), h('span', 'bk-num', String(cleared)), document.createTextNode('／'), h('span', 'bk-num', String(total)));
    const nodes: Node[] = [h('small', undefined, roman(idx + 1)), h('b', undefined, area.name), cnt];
    const chips = chipLabels(status);
    if (chips.length) {
      const c = h('span', 'bk-chip');
      for (const ch of chips) {
        const s = h('span', `bk-z-${ch.kind}`);
        if (ch.kind === 'honey' && status.honey) {
          s.append(document.createTextNode('蜜源'));
          const x = h('span', 'bk-x');
          x.append(h('small', undefined, '×'), document.createTextNode(String(status.honey.multiplier)));
          s.append(x);
        } else s.textContent = ch.text;
        c.append(s);
      }
      nodes.push(c);
    }
    this.head.replaceChildren(...nodes);
    this.page.dataset.hornet = status.hornet ? 'on' : 'off';
    this.page.dataset.duel = status.duel ? 'on' : 'off';
  }

  /** 並びは目次を開いた時点の現在地で決め、そのあいだは動かさない（見ている最中に行が入れ替わらないように） */
  private ensureLayout(area: StateArea): void {
    const spots = this.state?.spots.filter((s) => s.area_id === area.area_id) ?? [];
    if (this.layout && this.layout.areaId === area.area_id && this.layout.order.length === spots.length && spots.every((s) => this.layout?.order.includes(s.spot_id))) return;
    const f = geo.lastFix;
    const ordered = orderByDistance(spots, f ? { lat: f.lat, lon: f.lon } : null);
    this.layout = { areaId: area.area_id, order: ordered.map((o) => o.spot.spot_id) };
    const distances: Record<number, number> = {};
    for (const o of ordered) if (o.dist !== null) distances[o.spot.spot_id] = Math.round(o.dist);
    displayLog.setLayout({
      areaId: area.area_id,
      order: this.layout.order,
      orderedBy: f ? 'distance' : 'server',
      gpsStatus: geo.status,
      at: new Date().toISOString(),
      distances,
    });
    this.curPage = Math.min(this.curPage, Math.max(0, Math.ceil(spots.length / PAGE_SIZE) - 1));
  }

  private renderList(status: AreaStatus): void {
    const layout = this.layout;
    if (!layout || !this.state) return;
    const byId = new Map(this.state.spots.map((s) => [s.spot_id, s]));
    const ordered = layout.order.map((id) => byId.get(id)).filter((s): s is StateSpot => !!s);
    const pages = paginate(ordered, PAGE_SIZE);
    this.curPage = Math.min(this.curPage, pages.length - 1);
    // 中身が同じなら作り直さない（20秒ごとの状態の取り直しで行と印が一瞬消えないように）
    const key = `${layout.areaId}|${ordered.map((sp) => `${sp.spot_id}:${sp.name}:${sp.submitted ? 1 : 0}:${this.pendingSeal.has(sp.spot_id) ? 1 : 0}:${sealVisible(sp.submitted, status) ? 1 : 0}`).join(',')}`;
    if (key === this.listKey && this.sheets.length === pages.length) {
      if (!this.turning) this.sheets.forEach((s, p) => (s.hidden = p !== this.curPage));
      this.syncPager(pages.length);
      return;
    }
    this.listKey = key;
    this.turning = false;
    this.sheets = pages.map((rows, p) => {
      const sheet = h('div', 'bk-sheet');
      sheet.hidden = p !== this.curPage;
      for (const sp of rows) sheet.append(this.rowEl(sp, status));
      return sheet;
    });
    this.list.replaceChildren(...this.sheets);
    this.syncPager(pages.length);
  }

  private rowEl(sp: StateSpot, status: AreaStatus): HTMLElement {
    const done = sp.submitted;
    const pending = this.pendingSeal.has(sp.spot_id);
    const row = h('button', `bk-row${done ? ' done' : ''}`);
    row.type = 'button';
    row.dataset.spot = String(sp.spot_id);
    const tx = h('span', 'bk-tx');
    tx.append(h('b', undefined, sp.name));
    const dist = h('span', 'bk-dist');
    this.fillDistance(dist, sp);
    row.append(tx, dist, h('span', 'bk-go', '詳細'));
    if (pending || sealVisible(done, status)) {
      const slot = h('span', 'bk-slot');
      slot.dataset.slot = String(sp.spot_id);
      if (!pending) slot.append(this.sealImg(sp.spot_id));
      row.append(slot);
    }
    row.addEventListener('click', () => this.openDetail(sp.spot_id, true));
    return row;
  }

  /** 目次の印の img はスポットごとに1つを使い回す（作り直すと読み込み直しの間だけ消える） */
  private sealImg(spotId: number): HTMLImageElement {
    const kept = this.seals.get(spotId);
    if (kept) return kept;
    const img = this.newSealImg(spotId);
    this.seals.set(spotId, img);
    return img;
  }

  private newSealImg(spotId: number): HTMLImageElement {
    const img = h('img');
    img.src = this.ctx.asset('book/seal_weak.png');
    img.alt = 'クリア済み';
    img.style.setProperty('--r', `${sealAngle(spotId)}deg`);
    return img;
  }

  private fillDistance(dist: HTMLElement, sp: StateSpot): void {
    dist.replaceChildren();
    const f = geo.lastFix;
    if (!f) return;
    const t = fmtDistance(distanceM({ lat: f.lat, lon: f.lon }, { lat: sp.latitude, lon: sp.longitude }), geo.status);
    if (t.approx) dist.append(h('span', 'bk-ap', '約'));
    dist.append(h('span', 'bk-num', t.num), document.createTextNode(t.unit));
  }

  /** 位置が動いたら距離の数字だけ書き換える（並びは動かさない） */
  private refreshDistances(): void {
    const byId = new Map((this.state?.spots ?? []).map((s) => [s.spot_id, s]));
    this.list.querySelectorAll<HTMLElement>('.bk-row').forEach((row) => {
      const sp = byId.get(Number(row.dataset.spot));
      const dist = row.querySelector<HTMLElement>('.bk-dist');
      if (sp && dist) this.fillDistance(dist, sp);
    });
  }

  private syncPager(total: number): void {
    this.pager.hidden = total <= 1;
    (this.pager.querySelector('.cur') as HTMLElement).textContent = String(this.curPage + 1);
    (this.pager.querySelector('.tot') as HTMLElement).textContent = String(total);
    (this.pager.querySelector('.pv') as HTMLButtonElement).disabled = this.curPage === 0;
    (this.pager.querySelector('.nx') as HTMLButtonElement).disabled = this.curPage >= total - 1;
  }

  /** 頁をめくる。頁を左へ送り、次の頁を右から入れる（0.28秒） */
  private turn(d: number): void {
    const n = this.curPage + d;
    if (this.turning || n < 0 || n >= this.sheets.length) return;
    const a = this.sheets[this.curPage];
    const b = this.sheets[n];
    if (!a || !b) return;
    this.turning = true;
    a.classList.add(d > 0 ? 'out-l' : 'out-r');
    this.curPage = n;
    this.syncPager(this.sheets.length);
    setTimeout(() => {
      a.hidden = true;
      a.classList.remove('out-l', 'out-r');
      b.hidden = false;
      b.classList.add(d > 0 ? 'in-r' : 'in-l');
      requestAnimationFrame(() =>
        requestAnimationFrame(() => {
          b.classList.remove('in-r', 'in-l');
          this.turning = false;
        }),
      );
    }, 180);
  }

  // ------------------------------------------------------------ 詳細（㉚）

  private openDetail(spotId: number, fromToc: boolean): void {
    const sp = this.state?.spots.find((s) => s.spot_id === spotId);
    if (!sp || this.view === 'detail') return;
    if (this.areaId !== sp.area_id) this.setArea(sp.area_id);
    if (fromToc) displayLog.recordOpen(spotId, PAGE_SIZE); // 研究メモ：どの頁の何番目に見えていたか
    this.view = 'detail';
    this.detailId = spotId;
    this.book.dataset.view = 'detail';
    const token = ++this.viewSeq;
    void openView('spot', spotId, 'book').then((c) => {
      const close = c as (reason?: string) => void; // viewlog.ts の型は引数なしだが実体は reason を取る
      if (this.viewSeq === token && this.view === 'detail') this.closeView = close;
      else close('close');
    });
    this.renderDetail();
  }

  private closeDetail(reason: string): void {
    this.closeView?.(reason);
    this.closeView = null;
    this.viewSeq++;
    this.view = 'toc';
    this.detailId = null;
    this.book.dataset.view = 'toc';
    this.dpage.replaceChildren();
    if (this.visible) this.render();
  }

  private renderDetail(): void {
    const st = this.state;
    const sp = st?.spots.find((s) => s.spot_id === this.detailId);
    if (!st || !sp) return;
    const area = st.areas.find((a) => a.area_id === sp.area_id);
    const status = deriveStatus(st, sp.area_id, serverNow());
    const done = sp.submitted;

    const back = h('button', 'bk-back', '目次へ');
    back.type = 'button';
    back.addEventListener('click', () => {
      if (!this.submitting) this.closeDetail('back');
    });

    const body = h('div', 'bk-dbody');
    const ddata = h('dl', 'bk-ddata');
    const dm = h('dd');
    const mx = h('span', 'bk-x');
    mx.append(h('small', undefined, '×'), document.createTextNode(String(area?.multiplier ?? 1)));
    dm.append(mx);
    if (status.honey) {
      const hx = h('span', 'bk-x');
      hx.append(h('small', undefined, '×'), document.createTextNode(String(status.honey.multiplier)));
      dm.append(document.createTextNode('　蜜源'), hx);
    }
    const base = h('dd');
    base.append(h('span', 'bk-num', String(sp.base_point)), document.createTextNode(' 点'));
    ddata.append(h('dt', undefined, '基礎点'), base, h('dt', undefined, 'いまの倍率'), dm);
    body.append(h('h3', undefined, sp.name));
    if (sp.description) body.append(h('p', 'bk-ddesc', sp.description));
    body.append(ddata);

    const foot = h('div', 'bk-dfoot');
    const note = h('div', 'bk-dnote');
    if (status.hornet) note.append(this.slip('z-hornet', status.hornet.sourceName ? `スズメバチの襲来（${status.hornet.sourceName}）` : 'スズメバチの襲来', status.hornet.endsAt));
    if (status.honey) {
      const p = this.slip('z-honey', '蜜源', status.honey.endsAt);
      p.insertBefore(document.createTextNode('全チームの得点 ×' + status.honey.multiplier), p.querySelector('.rt'));
      note.append(p);
    }
    if (status.duel) {
      const p = this.slip('z-duel', `縄張り争い　${status.duel.mine}－${status.duel.theirs}${status.duel.opponentName ? ' ' + status.duel.opponentName : ''}`, status.duel.endsAt);
      p.insertBefore(document.createTextNode('縄張り争いのクリア数に入ります。'), p.querySelector('.rt'));
      note.append(p);
    }
    const map = h('button', 'bk-dmap', '地図で場所を確認');
    map.type = 'button';
    map.addEventListener('click', () => {
      if (this.submitting) return;
      this.closeDetail('to_map');
      this.ctx.switchTab('map', { spotId: sp.spot_id, areaId: sp.area_id });
    });
    const bs = submitButtonState(done, status);
    const go = h('button', `bk-dgo${bs.label.length > 10 ? ' long' : ''}`, bs.label);
    go.type = 'button';
    go.setAttribute('aria-disabled', String(!bs.enabled));
    go.addEventListener('click', () => {
      if (bs.enabled) void this.onSubmit(sp);
    });
    foot.append(note, map, go);

    this.dpage.replaceChildren(back, body);
    if (sealVisible(done, status)) {
      const seal = this.newSealImg(sp.spot_id);
      seal.className = 'bk-dseal';
      seal.style.removeProperty('--r');
      this.dpage.append(seal);
    }
    this.dpage.append(foot);
    this.tickRemaining();
  }

  /** 状態の札の1枚。右上に残り時間（1秒ごとに更新する） */
  private slip(cls: string, title: string, untilIso: string | null): HTMLElement {
    const p = h('p', cls);
    p.append(h('b', undefined, title));
    if (untilIso) {
      const rt = h('span', 'rt');
      rt.append(document.createTextNode('残り '));
      const n = h('span', 'bk-num');
      n.dataset.until = untilIso;
      n.textContent = fmtRemaining(remainingMs(untilIso));
      rt.append(n);
      p.append(rt);
    } else p.append(h('span', 'rt'));
    return p;
  }

  private startTick(): void {
    if (this.tick) return;
    this.tick = setInterval(() => this.tickRemaining(), 1000);
  }

  private tickRemaining(): void {
    let expired = false;
    this.dpage.querySelectorAll<HTMLElement>('[data-until]').forEach((n) => {
      const ms = remainingMs(n.dataset.until);
      n.textContent = fmtRemaining(ms);
      if (ms <= 0) expired = true;
    });
    if (expired && this.visible && this.locks === 0) this.render(); // 終わった状態をその場で外す
  }

  // ------------------------------------------------------------ 提出（㉛）と印（㉜）

  private async onSubmit(sp: StateSpot): Promise<void> {
    if (this.submitting) return;
    this.submitting = true;
    let outcome;
    try {
      // 呼び出しはクリックの操作の中で同期的に行う（カメラを開くため）
      outcome = await this.ctx.submitPhoto(this.el, {
        kind: 'spot',
        targetId: sp.spot_id,
        title: sp.name,
        areaId: sp.area_id,
        closeView: (reason) => {
          this.closeView?.(reason);
          this.closeView = null;
        },
      });
    } finally {
      this.submitting = false;
    }
    if (outcome.status === 'sent') {
      await this.afterSent(sp.spot_id, outcome.result);
    } else if (outcome.status === 'rejected') {
      await this.ctx.refresh(); // すでに誰かがクリアしていた等を画面に反映する
      if (this.view === 'detail') this.renderDetail();
    }
  }

  /** 送れた：目次へ戻り、初クリアなら印を押す（再クリア＝ダイスなしのときは印を押さない） */
  private async afterSent(spotId: number, result: SubmitResult): Promise<void> {
    const first = result.dice_value != null;
    if (first) this.pendingSeal.add(spotId);
    this.closeView = null;
    this.viewSeq++;
    this.view = 'toc';
    this.detailId = null;
    this.book.dataset.view = 'toc';
    this.dpage.replaceChildren();
    await this.ctx.refresh();
    this.showSpotOnToc(spotId);
    if (first) await this.pressSealFor(spotId);
    else this.render();
  }

  /** 目次のその行が見える頁を開く（並びはそのまま） */
  private showSpotOnToc(spotId: number): void {
    const sp = this.state?.spots.find((s) => s.spot_id === spotId);
    if (sp && this.areaId !== sp.area_id) this.setArea(sp.area_id);
    this.render();
    const i = this.layout?.order.indexOf(spotId) ?? -1;
    if (i >= 0) {
      this.curPage = positionOf(i).page;
      this.render();
    }
  }

  private async pressSealFor(spotId: number): Promise<void> {
    const status = this.state && this.areaId ? deriveStatus(this.state, this.areaId, serverNow()) : null;
    const slot = this.list.querySelector<HTMLElement>(`[data-slot="${spotId}"]`);
    this.locks++;
    try {
      // 頁を送った直後は目次が横に滑っている（.bk-sheet 0.18秒）。止まってから押さないと印の位置がずれる
      const moving = this.list.getAnimations({ subtree: true }).map((a) => a.finished.catch(() => undefined));
      await Promise.race([Promise.all(moving), sleep(400)]);
      // 縄張り争いの最中は印を外す約束（㉘）。押さずに印の扱いだけ戻す
      if (slot && !status?.duel) await this.ctx.fx.pressSeal(slot);
    } catch (e) {
      console.error(e);
    } finally {
      this.locks--;
      this.pendingSeal.delete(spotId);
      this.render();
    }
  }

  // ------------------------------------------------------------ 圏外から戻って送れた結果

  private async onSettled(out: SendOutcome): Promise<void> {
    await whenFlowIdle();
    if (out.status === 'queued') return;
    if (out.status === 'rejected') {
      this.ctx.notify({ heading: SETTLED_NOTICE_HEADING, body: settledNoticeBody(codeOf(out.error), 'spot') });
      await this.ctx.refresh();
      return;
    }
    const state = (await this.ctx.refresh()) ?? this.state;
    const sp = state?.spots.find((s) => s.spot_id === out.item.target_id);
    const dice = diceRequestFrom(out.result, sp?.base_point ?? null, sp?.name);
    if (!dice || !sp) {
      this.ctx.notify({ heading: SETTLED_NOTICE_HEADING, body: '送信待ちの写真を送りました' });
      return;
    }
    // 遅れて届いた初クリア：図鑑を開いてダイスを振り、印を押す
    this.pendingSeal.add(sp.spot_id);
    this.ctx.switchTab('book', { areaId: sp.area_id });
    await sleep(1000);
    if (this.view === 'detail') this.closeDetail('settled');
    this.showSpotOnToc(sp.spot_id);
    await this.ctx.fx.rollDice(this.el, dice);
    await this.pressSealFor(sp.spot_id);
  }

  // ------------------------------------------------------------ 開く演出（㉝ A 表紙が開く）

  private playOpen(): Promise<void> {
    if (window.matchMedia?.('(prefers-reduced-motion: reduce)').matches) return Promise.resolve();
    cancelAnimationFrame(this.raf);
    return new Promise((resolve) => {
      this.opn.hidden = false;
      const set = (a: number): void => {
        this.cover.style.transform = `rotateY(${-a}deg)`;
        this.shade.style.opacity = (a < 90 ? 0.9 * Math.cos((a * Math.PI) / 180) : 0).toFixed(3);
      };
      set(0);
      const t0 = performance.now();
      const step = (now: number): void => {
        const p = Math.max(0, Math.min(1, (now - t0) / OPEN_MS));
        set(100 * ease.io(p)); // 0〜100度。ゆっくり始まりゆっくり終わる
        if (p < 1) this.raf = requestAnimationFrame(step);
        else {
          this.opn.hidden = true;
          resolve();
        }
      };
      this.raf = requestAnimationFrame(step);
    });
  }
}

export const createBookTab: TabFactory = (ctx) => new BookTab(ctx);
export { displayLog } from './displayLog';
